/**
 * Created by jonas on 2020-10-01.
 */


import {gsap} from "gsap";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {BasicSymbol} from "../../../game/reels/symbols/BasicSymbol";
import {ScatterSymbol} from "../../../game/reels/symbols/ScatterSymbol";
import {XwaysHoarder2} from "../../../game/XwaysHoarder2";


export interface Positionable {
    position: {
        x: number,
        y: number
    },
    isSticky?: boolean
}

export interface SimpleReelModelSymbolPosition {
    reelID: number;
    position: number
}

export interface SimpleReelModelDelegate {
    createSymbol(symbolName: string, state: SimpleReelModelState, position: SimpleReelModelSymbolPosition, symbolSize: number): Positionable;

    removeSymbol(symbolData: SimpleReelModelSymbolData, state: SimpleReelModelState): void;
}

export interface SimpleReelModelSymbolData {
    name: string,
    positionable: Positionable,
    reelPosition: number,
    reelId: number,
    symbolSize: number
}

export enum SimpleReelModelState {
    IDLE = "IDLE",
    START = "START",
    SPIN = "SPIN",
    STOP = "STOP",
    STOPPED = "STOPPED",
    BOUNCE = "BOUNCE",
    WIND_UP = "WIND_UP",
    NUDGE = "NUDGE",
    STICKY = "STICKY",
    REMOVED = "REMOVED"
}

export interface SimpleReelModelEventData {
    reel: number;
    state: SimpleReelModelState;
}

export interface SimpleReelPaddingData {
    visualPaddingBefore: number;
    paddingBefore: number;
    visibleSymbols: number;
    paddingAfter: number;
    visualPaddingAfter: number;
}

export class SimpleReelModel {
    public illegalVisualPaddingSymbols: string[] = [];
    public paddingConfig: SimpleReelPaddingData = {
        visualPaddingBefore: 2,
        paddingBefore: 0,
        visibleSymbols: 3,
        paddingAfter: 0,
        visualPaddingAfter: 2,
    }
    public animatedStepData = {
        y: 0
    }
    public stepSize: number = 180;
    public stepDuration = 0.1;
    public readonly id: number;
    public _delegate: SimpleReelModelDelegate;
    /**
     * Triggers on: ReelStateChange and each ReelModelState
     *
     *
     */
    public readonly events: APIEventSystem | undefined;
    public visibleNudgeSymbols: (BasicSymbol | undefined) [] = [
        undefined, undefined, undefined
    ];
    private _triggerTime: number;
    private _reelSetIndex: number = 0;
    private _stopIndex: number;
    private _stopSymbols: SimpleReelModelSymbolData[] = [];
    private _spinning: boolean = false;
    private _currentSpinStep: gsap.core.Tween | undefined;
    private animationTimeScale = 1;
    private _stopping: boolean = false;
    private triggerStop: boolean = false;
    private _startedReverse: boolean;
    private masterSpinTimeline: gsap.core.Timeline | undefined;

    constructor(id: number, delegate: SimpleReelModelDelegate, triggerEvents: boolean = false) {
        this.id = id;
        this._delegate = delegate;
        if (triggerEvents) {
            this.events = SlotGame.apiPlugin.eventSystemFactory.create();
            this.events.on("ReelStateChange", (data: SimpleReelModelEventData) => this.onReelStateChange(data)); //To prevent event system from saving once callbacks if no on callbacks is set.
        }
    }

    private _stopTriggerTl: gsap.core.Timeline | undefined;

    get stopTriggerTl(): gsap.core.Timeline | undefined {
        return this._stopTriggerTl;
    }

//Fake reel
    private _reelSet: string[] | undefined;

    get reelSet(): string[] {
        if (!this._reelSet) {
            throw new Error(`No reelset set for reel: ${this.id}`);
        }
        return this._reelSet;
    }

    private _symbols: SimpleReelModelSymbolData[] = [];

    get symbols(): SimpleReelModelSymbolData[] {
        return this._symbols;
    }

    private _LTarray: SimpleReelModelSymbolData[] = [];

    get LTarray(): SimpleReelModelSymbolData[] {
        return this._LTarray;
    }

    set LTarray(value: SimpleReelModelSymbolData[]) {
        this._LTarray = value;
    }

    private _scatterArray: SimpleReelModelSymbolData[] = [];

    get scatterArray(): SimpleReelModelSymbolData[] {
        return this._scatterArray;
    }

    set scatterArray(value: SimpleReelModelSymbolData[]) {
        this._scatterArray = value;
    }

    private _xwaysArray: SimpleReelModelSymbolData[] = [];

    get xwaysArray(): SimpleReelModelSymbolData[] {
        return this._xwaysArray;
    }

    set xwaysArray(value: SimpleReelModelSymbolData[]) {
        this._xwaysArray = value;
    }

    private _xsplitArray: SimpleReelModelSymbolData[] = [];

    get xsplitArray(): SimpleReelModelSymbolData[] {
        return this._xsplitArray;
    }

    set xsplitArray(value: SimpleReelModelSymbolData[]) {
        this._xsplitArray = value;
    }

    private _stopTimeline: gsap.core.Timeline | undefined;

    get stopTimeline(): gsap.core.Timeline | undefined {
        return this._stopTimeline;
    }

    private _speed: number = this.animationTimeScale / this.stepDuration;

    get speed(): number {
        return this._speed;
    }

    set speed(value: number) {
        this._speed = value;
        this.animationTimeScale = value * this.stepDuration;
        this.masterSpinTimeline?.timeScale(this.animationTimeScale);
    }

    private _state: SimpleReelModelState = SimpleReelModelState.IDLE;

    get state(): SimpleReelModelState {
        return this._state;
    }

    set state(value: SimpleReelModelState) {
        this._state = value;
        if (this.events) {
            this.events.trigger("ReelStateChange", <SimpleReelModelEventData>{reel: this.id, state: this._state});
        }
    }

    public setReelSet(reelSet: string[], index?: number) {
        this._reelSet = reelSet;
        index = index != undefined ? index : Math.floor(Math.random() * this._reelSet.length);
        this.setReelIndex(index);
    }

    public init(symbols: string[]) {
        this.clearSymbols(this._symbols);
        this.clearSymbols(this._stopSymbols);


        //if(this.id === 1) {
        //  symbols[0] = "XW";
        //}

        const totalCount = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter;
        const totalInitCount = this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter;

        if (symbols.length != totalInitCount) {
            Logger.warn(`ReelModel.init:  Symbol count mismatch in reel ${this.id}`);
        }

        symbols = this.getRandomSymbols(this.paddingConfig.visualPaddingBefore).concat(symbols);
        symbols = symbols.concat(this.getRandomSymbols(this.paddingConfig.visualPaddingAfter));

        const initSizes:number[][]=JSON.parse(JSON.stringify(XwaysHoarder2.model.initData.symbolSizes));
        initSizes.forEach((reel: number[]) => {
            reel.unshift(1);
            reel.unshift(1);
            reel.push(1);
            reel.push(1);
        })
        for (let i: number = 0; i < symbols.length; i++) {
            const symbol = this.createNewSymbol(symbols[i], i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore, SimpleReelModelState.IDLE, initSizes[this.id][i]);
            this._symbols.push(symbol);
        }

        this.updatePosition();
    }

    public halt() {
        if (this._currentSpinStep) {
            this.masterSpinTimeline?.kill();
            this._currentSpinStep?.progress(1);
        }
    }

    public resume() {
        this.startSpin();
    }

    public stop(stopSymbolNames: string[], reverse: boolean = false, splitValue = 1): gsap.core.Timeline {
        reverse = this._startedReverse;

        this.clearSymbols(this._stopSymbols);
        this._stopSymbols = [];
        const numberOfNormalSteps = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter;

        this.illegalVisualPaddingSymbols = [];
        this.illegalVisualPaddingSymbols.length = 0;
        if (stopSymbolNames.includes("SSC") || stopSymbolNames.includes("SC")) {
            this.illegalVisualPaddingSymbols.push("SC")
        }
        if (stopSymbolNames.includes("XW")) {
            this.illegalVisualPaddingSymbols.push("XW")
        }
        if (stopSymbolNames.includes("XN")) {
            this.illegalVisualPaddingSymbols.push("XN")
        }


        while (stopSymbolNames.length < numberOfNormalSteps) {
            stopSymbolNames.unshift(this.getRandomSymbolVisualPaddingSymbol());
            stopSymbolNames.push(this.getRandomSymbolVisualPaddingSymbol());
        }


        /*        stopSymbolNames = this.getRandomSymbols(this.paddingConfig.visualPaddingBefore).concat(stopSymbolNames);
                stopSymbolNames = stopSymbolNames.concat(this.getRandomSymbols(this.paddingConfig.visualPaddingAfter));*/


        for (let i: number = stopSymbolNames.length - 1; i >= 0; i--) {
            const symbol = this.createNewSymbol(stopSymbolNames[i], i - this.paddingConfig.visualPaddingBefore - this.paddingConfig.paddingBefore, SimpleReelModelState.STOP, 1);
            symbol.positionable.position.y = this.stepSize * -(this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore + this.paddingConfig.visibleSymbols);
            this._stopSymbols.unshift(symbol);
        }
        //console.log(this._stopSymbols);

        this._stopTriggerTl = gsap.timeline({
            paused: true,
            onStart: () => {
                this.state = SimpleReelModelState.STOP;
                this._spinning = false;
                this.triggerStop = true;
                this._triggerTime = Date.now();
            }
        });

        this._stopTimeline = gsap.timeline({
            paused: true,
            onStart: () => {
                //  console.log(Date.now() - this._triggerTime);
                this._stopIndex = stopSymbolNames.length - 1;
                this._stopping = true;
            },
            onComplete: () => {
                this.state = SimpleReelModelState.STOPPED;
                this._stopTimeline = undefined;
                this._stopSymbols = [];
                this._stopping = false;
                this.state = SimpleReelModelState.IDLE;
            }
        });


        for (let i: number = 0; i < numberOfNormalSteps; i++) {
            const tween = this.step(reverse);
            this._stopTimeline.add(tween);
        }

        this._stopTimeline.add(this.bounce());
        /*      this._stopTimeline.add(()=>{
                  this.stickySymbolMoveBacktoSymbols(this.scatterArray);
              },this._stopTimeline.totalDuration());*/

        this._stopTriggerTl.add(gsap.to(this, {duration: this._stopTimeline.totalDuration()}));

        return this._stopTriggerTl.play(0);
    }

    public start(reverse: boolean = false): gsap.core.Timeline {
        this._startedReverse = reverse;
        const tl: gsap.core.Timeline = gsap.timeline({
            onStart: () => {
                this.state = SimpleReelModelState.START;
                this._stopSymbols = [];
            },
            onComplete: () => {
                this.startSpin(reverse);
            }
        });

        const tween = this.step(reverse);
        tween.vars.ease = "back.in(3)";
        tween.totalDuration(this.stepDuration * 8);

        tl.add(tween);

        return tl;
    }

    public windUp(windFactor: number = 0.2, durationFactor: number = 1, reverse: boolean = false): gsap.core.Timeline {
        const tl = gsap.timeline();
        const wind = gsap.to(this.animatedStepData, {
            duration: this.stepDuration * durationFactor,
            y: reverse ? this.stepSize * windFactor : -this.stepSize * windFactor,
            ease: "power2.out",
            onStart: () => {
                this.state = SimpleReelModelState.WIND_UP;
            },
            onUpdate: () => this.updatePosition(),
        });

        const back = gsap.to(this.animatedStepData, {
            duration: this.stepDuration * durationFactor,
            y: 0,
            ease: "power2.in",
            onUpdate: () => this.updatePosition(),
        });

        tl.add(wind);
        tl.add(back);

        return tl;
    }

    public bounce(bounceFactor: number = 0.2, durationFactor: number = 1, reverse: boolean = false): gsap.core.Timeline {
        const tl = gsap.timeline();
        const overshoot = gsap.to(this.animatedStepData, {
            duration: this.stepDuration * durationFactor,
            y: reverse ? -this.stepSize * bounceFactor : this.stepSize * bounceFactor,
            ease: "power2.out",
            onStart: () => {
                this.state = SimpleReelModelState.BOUNCE;
            },
            onUpdate: () => this.updatePosition(),
        });

        const back = gsap.to(this.animatedStepData, {
            duration: this.stepDuration * durationFactor * 2,
            y: 0,
            ease: "power2.out",
            onUpdate: () => this.updatePosition(),
        });

        tl.add(overshoot);
        tl.add(back);

        return tl;
    }


    //Public functions

    public nudge(reverse: boolean = false): gsap.core.Tween {
        const tween = this.step(reverse);
        tween.eventCallback("onStart", () => {
            this.state = SimpleReelModelState.NUDGE;
            this.onAnimationStart(tween, reverse);
        });

        return tween;
    }

    public forcePositionUpdate() {
        this.updatePosition();
    }


    public resize(stepSize: number, duration: number, ease: gsap.EaseFunction): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({onUpdate: () => this.updatePosition()});
        tl.add(gsap.to(this, {duration: duration, ease: ease, stepSize: stepSize}))
        return tl;
    }

    public visibleIndexToRealIndex(visibleIndex: number) {
        const paddingBefore = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore;
        return visibleIndex + paddingBefore;
    }

    public getAllVisibleSymbols(): SimpleReelModelSymbolData [] {
        const paddingBefore = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore;
        if (this._stopSymbols.length > 0) {
            return this._stopSymbols.slice(paddingBefore, paddingBefore + this.paddingConfig.visibleSymbols);
        } else {
            return this._symbols.slice(paddingBefore, paddingBefore + this.paddingConfig.visibleSymbols);
        }

    }

    public updateVisibleNudgeSymbols(pos: number, symData: SimpleReelModelSymbolData) {
        const paddingBefore = this.paddingConfig.visualPaddingBefore + this.paddingConfig.paddingBefore;
        if (pos > -1 && pos < 3) {
            this.visibleNudgeSymbols[pos] = <BasicSymbol>symData.positionable;
        }
    }

    public getVisibleSymbolAt(symbolIndex: number): SimpleReelModelSymbolData {
        const index = this.visibleIndexToRealIndex(symbolIndex);

        if (this._stopSymbols.length > 0) {
            return this._stopSymbols[index];
        } else {
            return this._symbols[index];
        }
    }

    public getDataForPositionable(positionable: Positionable): SimpleReelModelSymbolData | undefined {
        const symbols = this.getAllVisibleSymbols();
        for (let symbolData of symbols) {
            if (symbolData.positionable == positionable) {
                return symbolData;
            }
        }

        return;
    }

    public moveStickyXSBack(): void {
        if (this.xsplitArray.length > 0) {
            this.xsplitArray.forEach((sym: SimpleReelModelSymbolData, index: number) => {
                this.moveStickyBackToSymbols(sym)
                //console.log("moving back", currentSyms);

            });
        }
    }


    public moveStickyXWBack(): void {
        if (this.xwaysArray.length > 0) {
            this.xwaysArray.forEach((sym: SimpleReelModelSymbolData, index: number) => {
                this.moveStickyBackToSymbols(sym)
                //console.log("moving back", currentSyms);

            });
        }
    }

    public moveStickyLTBack(): void {
        if (this.LTarray.length > 0) {
            this.LTarray.forEach((sym: SimpleReelModelSymbolData, index: number) => {
                this.moveStickyBackToSymbols(sym)
                //console.log("moving back", currentSyms);

            });
        }
    }

    public moveStickySCBack(): void {
        if (this.scatterArray.length > 0) {
            this.scatterArray.forEach((sym: SimpleReelModelSymbolData, index: number) => {
                this.moveStickyBackToSymbols(sym)
                //console.log("moving back", currentSyms);

            });
        }
    }

    private moveStickyBackToSymbols(symData: SimpleReelModelSymbolData): void {
        const currenTSym: BasicSymbol = <BasicSymbol>symData.positionable;
        const currentIndex: number = this.visibleIndexToRealIndex(currenTSym.symID);
        const oldSym: SimpleReelModelSymbolData[] = this.symbols.splice(currentIndex, 1, symData);
        this.removeSymbol();
    }

    private onReelStateChange(data: SimpleReelModelEventData): void {
        this.events?.trigger(data.state, data);
    }

    private getNextSymbolName(nextSymbolPosition: number): string {
        let nextIndex = this._reelSetIndex + nextSymbolPosition;
        if (nextIndex < 0) {
            nextIndex = this.reelSet.length + nextIndex;
        }
        if (nextIndex >= this.reelSet.length) {
            nextIndex = nextIndex - this.reelSet.length;
        }
        return this.reelSet[nextIndex];

    }

    private getNextSymbol(position: number, state: SimpleReelModelState): SimpleReelModelSymbolData {
        //Stopping
        if (this._stopping && this._stopSymbols.length > 0) {
            const symbol = this._stopSymbols[this._stopIndex];
            symbol.reelPosition = position;
            return symbol;
        }
        //Default
        return this.createNewSymbol(this.getNextSymbolName(position), position, state, 1);
    }

    private createNewSymbol(name: string, position: number, state: SimpleReelModelState, symbolSize: number): SimpleReelModelSymbolData {
        const symbol: SimpleReelModelSymbolData = {
            name: name,
            positionable: this._delegate.createSymbol(name, state, {reelID: this.id, position: position}, symbolSize),
            reelPosition: position,
            reelId: this.id,
            symbolSize: symbolSize
        }
        return symbol;
    }

    private getRandomSymbolVisualPaddingSymbol(): string {
        const randomIndex = Math.floor(Math.random() * this.reelSet.length);
        let symbol = this.reelSet[randomIndex];
        if (this.illegalVisualPaddingSymbols.indexOf(symbol) >= 0) {
            symbol = this.getRandomSymbolVisualPaddingSymbol();
        }
        return symbol;
    }

    private getRandomSymbolInReelSet(): string {
        const randomIndex = Math.floor(Math.random() * this.reelSet.length);
        return this.reelSet[randomIndex];
    }

    private clearSymbols(symbols: SimpleReelModelSymbolData[]): void {
        let symbol;
        while (symbol = symbols.pop()) {
            this._delegate.removeSymbol(symbol, this.state);
        }
    }

    private getRandomSymbols(count: number): string[] {
        const result: string[] = [];
        for (let i: number = 0; i < count; i++) {
            result.push(this.getRandomSymbolVisualPaddingSymbol());
        }
        return result;
    }

    private startSpin(reverse: boolean = false) {
        this._spinning = true;
        this.state = SimpleReelModelState.SPIN;
        this.masterSpinTimeline = gsap.timeline();
        this.masterSpinTimeline.add(this.spin(reverse));
    }

    private spin(reverse: boolean = false): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline({
            onComplete: () => {
                if (this._spinning) {
                    this.masterSpinTimeline?.add(this.spin(reverse));
                    //this.spin(reverse);
                }
            }
        })
        tl.add(this.step(reverse));
        return tl;
    }

    private step(reverse: boolean = false): gsap.core.Tween {
        const tween: gsap.core.Tween = gsap.to(this.animatedStepData, {
            duration: this.stepDuration * 0.75,
            y: reverse ? -this.stepSize : this.stepSize,
            ease: "none",
            onStart: () => this.onAnimationStart(tween, reverse),
            onUpdate: () => this.updatePosition(),
            onComplete: () => this.onAnimationComplete(reverse)
        });
        return tween;
    }

    private onAnimationStart(tween: gsap.core.Tween, reverse: boolean = false) {
        this._currentSpinStep = tween;
        this.addSymbol(reverse);
    }

    private onAnimationComplete(reverse: boolean = false) {
        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol = this._symbols[i];
            symbol.reelPosition += reverse ? -1 : 1;
        }

        this.animatedStepData.y = 0;

        if (this.triggerStop) {
            this.triggerStop = false;
            this.masterSpinTimeline?.kill();
            this._stopTimeline?.play(0);
        }

        this.updateIndex(reverse);
        this.removeSymbol();
        this.updatePosition();
    }

    private updateIndex(reverse: boolean) {
        const valueChange = reverse ? 1 : -1
        if (this._stopping) {
            this.setStopIndex(this._stopIndex + valueChange);
        } else {
            this.setReelIndex(this._reelSetIndex + valueChange);
        }
    }

    private setStopIndex(value: number) {
        if (value >= this._stopSymbols.length) {
            value = 0;
        } else if (value < 0) {
            value = this._stopSymbols.length - 1;
        }
        this._stopIndex = value;
    }

    private setReelIndex(value: number) {
        if (value >= this.reelSet.length) {
            value = 0;
        } else if (value < 0) {
            value = this.reelSet.length - 1;
        }
        this._reelSetIndex = value;
    }

    private addSymbol(reverse: boolean) {
        let newSymbols = [];
        let newSymbolPosition = this._symbols[0].reelPosition - 1;

        if (reverse) {
            newSymbolPosition = this._symbols[this._symbols.length - 1].reelPosition + 1;
        }

        newSymbols.push(this.getNextSymbol(newSymbolPosition, this.state));

        this._symbols = this._symbols.concat(newSymbols);
        this._symbols.sort((a: any, b: any) => {
            return (a.reelPosition < b.reelPosition) ? -1 : 1;
        });
    }

    private removeSymbol() {
        for (let i: number = this._symbols.length - 1; i >= 0; i--) {
            const symbol = this._symbols[i];
            if (symbol.reelPosition >= this.paddingConfig.visibleSymbols + this.paddingConfig.paddingAfter + this.paddingConfig.visualPaddingAfter) {
                //Remove from bottom
                const removedSymbol = this._symbols.splice(i, 1)[0];

                if (!removedSymbol.positionable.isSticky)
                    this._delegate.removeSymbol(removedSymbol, this.state);


            }
            if (symbol.reelPosition < -this.paddingConfig.paddingBefore - this.paddingConfig.visualPaddingBefore) {
                //Remove from top
                const removedSymbol = this._symbols.splice(i, 1)[0];

                if (!removedSymbol.positionable.isSticky)
                    this._delegate.removeSymbol(removedSymbol, this.state);

            }
        }
    }

    private updatePosition() {
        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol = this._symbols[i];
            const basePos = this.stepSize * symbol.reelPosition;

            if (!symbol.positionable.isSticky) {
                symbol.positionable.position.y = basePos + this.animatedStepData.y;
            }
        }
    }

}